/**
 * Sección Trading Bot — dashboard live de la cuenta paper.
 *
 * Muestra:
 *  - Equity actual + balance + P&L día/semana/mes + drawdown actual
 *  - Curva de equity (sparkline simple)
 *  - Posiciones abiertas con P&L flotante
 *  - Últimas señales (con razón)
 *  - Stats: winrate, profit factor, expectancy
 *  - Controles: profile, símbolos, kill switch, resume, reset, tick manual
 */
import { h, apiClient, formatEur, pill, btn, toast, emptyState } from './_common.js';
import { renderEquityCurveChart, renderMonthlyPnlChart, renderSymbolWinrateChart, renderDrawdownChart, renderBacktestEquityChart } from './_charts.js';

export async function renderTrading(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'max-w-7xl mx-auto px-6 py-6 space-y-6' });
  root.appendChild(wrap);

  wrap.appendChild(h('div', { class: 'flex items-center justify-between' }, [
    h('div', {}, [
      h('h2', { class: 'font-display text-2xl font-bold' }, '🤖 Trading Bot'),
      h('p', { class: 'text-sm text-ink-300' }, 'Paper-trading en tiempo real — Forex / Oro / Cripto. Datos reales, ejecución simulada.'),
    ]),
    h('div', { class: 'flex gap-2' }, [
      btn('🔄 Forzar tick', { class: 'bg-base-700 hover:bg-base-600 text-ink-100 text-sm px-3 py-1.5 rounded-md', onclick: async () => {
        const t = toast('Ejecutando tick...');
        try {
          await apiClient.post('/api/v1/trading/tick', {});
          t.update('Tick OK', 'success');
          await renderTrading(root);
        } catch (e) {
          t.update('Tick falló: ' + (e?.message || e), 'error');
        }
      } }),
      btn('🛑 Kill Switch', { class: 'bg-red-600/80 hover:bg-red-600 text-white text-sm px-3 py-1.5 rounded-md', onclick: async () => {
        if (!confirm('¿Activar kill switch? Cierra TODAS las posiciones abiertas y deshabilita el bot.')) return;
        await apiClient.post('/api/v1/trading/kill-switch', { reason: 'Manual desde dashboard' });
        toast('Kill switch activado', 'warning');
        await renderTrading(root);
      } }),
    ]),
  ]));

  // Init cuenta si no existe
  await apiClient.post('/api/v1/trading/init', {}).catch(() => null);

  const [accountRes, posRes, sigRes, statsRes, eqRes] = await Promise.all([
    apiClient.get('/api/v1/trading/account').catch(() => ({ account: null })),
    apiClient.get('/api/v1/trading/positions').catch(() => ({ open: [], closed: [] })),
    apiClient.get('/api/v1/trading/signals').catch(() => ({ signals: [] })),
    apiClient.get('/api/v1/trading/stats').catch(() => null),
    apiClient.get('/api/v1/trading/equity-curve').catch(() => ({ snapshots: [] })),
  ]);

  const acc = accountRes.account;
  if (!acc) {
    wrap.appendChild(emptyState('No se pudo cargar la cuenta', 'Revisa que el backend trading esté arrancado.'));
    return;
  }

  // ===== KPI Cards =====
  const pnlDayColor = acc.dailyPnl >= 0 ? 'text-emerald-400' : 'text-red-400';
  const pnlMonthColor = acc.monthlyPnl >= 0 ? 'text-emerald-400' : 'text-red-400';
  const ddColor = acc.maxDrawdown >= acc.maxOverallDrawdownPct * 0.7 ? 'text-red-400' : 'text-amber-400';
  const equityColor = acc.equity >= acc.startingBalance ? 'text-emerald-400' : 'text-red-400';

  wrap.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-3' }, [
    kpi('Equity', formatEur(acc.equity), equityColor, `Balance: ${formatEur(acc.balance)}`),
    kpi('P&L Hoy', `${acc.dailyPnl >= 0 ? '+' : ''}${formatEur(acc.dailyPnl)}`, pnlDayColor, `${((acc.dailyPnl / acc.equity) * 100).toFixed(2)}%`),
    kpi('P&L Mes', `${acc.monthlyPnl >= 0 ? '+' : ''}${formatEur(acc.monthlyPnl)}`, pnlMonthColor, `ROI total: ${(((acc.equity - acc.startingBalance) / acc.startingBalance) * 100).toFixed(2)}%`),
    kpi('Drawdown', `${acc.maxDrawdown.toFixed(2)}%`, ddColor, `Límite: ${acc.maxOverallDrawdownPct}%`),
    kpi('Trades', `${acc.totalTrades}`, 'text-ink-200', `${acc.winningTrades}W / ${acc.losingTrades}L`),
  ]));

  // ===== Historial COMPLETO de operaciones (con filtros + modal detalle) =====
  await appendHistoryPanel(wrap).catch((e) => {
    console.error('[history-panel] error:', e);
    wrap.appendChild(document.createElement('div')).textContent = 'Error cargando historial: ' + (e?.message || e);
  });

  // ===== Estado bot =====
  const status = acc.killSwitchActive
    ? { label: 'KILL SWITCH ACTIVO', color: 'bg-red-500/20 text-red-300 border-red-400/40' }
    : !acc.enabled
    ? { label: 'PAUSADO', color: 'bg-amber-500/20 text-amber-300 border-amber-400/40' }
    : { label: 'OPERANDO', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' };

  wrap.appendChild(h('div', { class: 'flex items-center justify-between bg-base-800/50 border border-base-700 rounded-xl px-4 py-3' }, [
    h('div', { class: 'flex items-center gap-3' }, [
      h('span', { class: `inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${status.color}` }, [
        h('span', { class: 'w-1.5 h-1.5 rounded-full bg-current animate-pulse' }),
        status.label,
      ]),
      h('span', { class: 'text-sm text-ink-300' }, `Profile: ${acc.profile} · Riesgo/trade: ${acc.riskPerTradePct}% · Max trades: ${acc.maxConcurrentTrades}`),
    ]),
    h('div', { class: 'flex gap-2' }, [
      acc.killSwitchActive && btn('▶️ Reanudar', { class: 'bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs px-3 py-1 rounded-md', onclick: async () => {
        await apiClient.post('/api/v1/trading/resume', {});
        toast('Bot reanudado', 'success');
        await renderTrading(root);
      } }),
      btn('🔄 Reset', { class: 'bg-base-700 hover:bg-base-600 text-ink-300 text-xs px-3 py-1 rounded-md', onclick: async () => {
        if (!confirm('Reset cuenta paper a balance inicial? Cancela todas las posiciones abiertas.')) return;
        await apiClient.post('/api/v1/trading/reset', {});
        toast('Cuenta reseteada', 'success');
        await renderTrading(root);
      } }),
    ]),
  ]));

  // ===== Charts profesionales Chart.js =====
  if (eqRes.snapshots && eqRes.snapshots.length > 0) {
    const chartsRow = h('div', { class: 'grid grid-cols-1 lg:grid-cols-2 gap-3' });
    const equityBox = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('div', { class: 'flex items-center justify-between mb-2' }, [
        h('h3', { class: 'font-semibold text-sm' }, '📈 Curva de Equity'),
        h('span', { class: 'text-xs text-ink-400' }, `${eqRes.snapshots.length} snapshots`),
      ]),
    ]);
    const equityCanvas = h('div', { class: 'relative w-full', style: 'height: 280px' });
    equityBox.appendChild(equityCanvas);
    chartsRow.appendChild(equityBox);

    const ddBox = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('div', { class: 'flex items-center justify-between mb-2' }, [
        h('h3', { class: 'font-semibold text-sm' }, '⚠️ Drawdown'),
        h('span', { class: 'text-xs text-ink-400' }, `Max: ${acc.maxDrawdown.toFixed(2)}%`),
      ]),
    ]);
    const ddCanvas = h('div', { class: 'relative w-full', style: 'height: 280px' });
    ddBox.appendChild(ddCanvas);
    chartsRow.appendChild(ddBox);

    wrap.appendChild(chartsRow);

    renderEquityCurveChart(equityCanvas, eqRes.snapshots).catch((e) => console.error('equity chart:', e));
    renderDrawdownChart(ddCanvas, eqRes.snapshots).catch((e) => console.error('dd chart:', e));
  }

  // P&L mensual + Distribución símbolo
  if (posRes.closed && posRes.closed.length > 0) {
    const chartsRow2 = h('div', { class: 'grid grid-cols-1 lg:grid-cols-2 gap-3' });
    const monthlyBox = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h3', { class: 'font-semibold text-sm mb-2' }, '📊 P&L mensual'),
    ]);
    const monthlyCanvas = h('div', { class: 'relative w-full', style: 'height: 220px' });
    monthlyBox.appendChild(monthlyCanvas);
    chartsRow2.appendChild(monthlyBox);

    const symBox = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h3', { class: 'font-semibold text-sm mb-2' }, '🎯 P&L por símbolo'),
    ]);
    const symCanvas = h('div', { class: 'relative w-full', style: 'height: 240px' });
    symBox.appendChild(symCanvas);
    chartsRow2.appendChild(symBox);

    wrap.appendChild(chartsRow2);

    renderMonthlyPnlChart(monthlyCanvas, posRes.closed).catch((e) => console.error('monthly:', e));
    // Compute bySymbol localmente
    const bySymMap = new Map();
    for (const t of posRes.closed) {
      if (!bySymMap.has(t.symbol)) bySymMap.set(t.symbol, 0);
      bySymMap.set(t.symbol, bySymMap.get(t.symbol) + (t.realizedPnl || 0));
    }
    const bySym = Array.from(bySymMap.entries()).map(([symbol, pnl]) => ({ symbol, pnl }));
    renderSymbolWinrateChart(symCanvas, bySym).catch((e) => console.error('symbol:', e));
  }

  // ===== Stats =====
  if (statsRes && statsRes.totalTrades > 0) {
    const s = statsRes;
    wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h3', { class: 'font-semibold text-sm mb-3' }, '📊 Métricas de rendimiento'),
      h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 text-sm' }, [
        statBox('Win Rate', `${s.winRatePct.toFixed(1)}%`, s.winRatePct >= 55 ? 'text-emerald-400' : 'text-amber-400'),
        statBox('Profit Factor', s.profitFactor.toFixed(2), s.profitFactor >= 1.5 ? 'text-emerald-400' : 'text-amber-400'),
        statBox('Avg Win', formatEur(s.avgWin), 'text-emerald-400'),
        statBox('Avg Loss', formatEur(s.avgLoss), 'text-red-400'),
        statBox('Expectancy', formatEur(s.expectancy), s.expectancy > 0 ? 'text-emerald-400' : 'text-red-400'),
        statBox('Gross Profit', formatEur(s.grossProfit), 'text-ink-200'),
        statBox('Gross Loss', formatEur(s.grossLoss), 'text-ink-200'),
        statBox('Net P&L', formatEur(s.netPnl), s.netPnl >= 0 ? 'text-emerald-400' : 'text-red-400'),
      ]),
    ]));
  }

  // ===== Posiciones abiertas =====
  const openHeader = h('div', { class: 'flex items-center justify-between mb-2' }, [
    h('h3', { class: 'font-semibold' }, `🔵 Posiciones abiertas (${posRes.open.length})`),
    posRes.open.length > 0 && btn('Cerrar todas', { class: 'bg-base-700 hover:bg-base-600 text-xs px-2 py-1 rounded', onclick: async () => {
      if (!confirm('¿Cerrar todas las posiciones abiertas?')) return;
      await apiClient.post('/api/v1/trading/close-all', {});
      await renderTrading(root);
    } }),
  ]);
  const openList = posRes.open.length === 0
    ? h('div', { class: 'text-ink-400 text-sm bg-base-800/30 border border-base-700 rounded-lg p-4 text-center' }, 'Sin posiciones abiertas. Bot escanea cada 5min.')
    : h('div', { class: 'space-y-2' }, posRes.open.map((p) => positionCard(p)));
  wrap.appendChild(h('div', {}, [openHeader, openList]));

  // ===== Últimas señales =====
  if (sigRes.signals && sigRes.signals.length > 0) {
    wrap.appendChild(h('div', {}, [
      h('h3', { class: 'font-semibold mb-2' }, `📡 Señales recientes (${sigRes.signals.length})`),
      h('div', { class: 'space-y-2' }, sigRes.signals.slice(0, 15).map((s) => signalCard(s))),
    ]));
  }

  // ===== Últimos trades cerrados =====
  if (posRes.closed && posRes.closed.length > 0) {
    wrap.appendChild(h('div', {}, [
      h('h3', { class: 'font-semibold mb-2' }, `📋 Últimos trades cerrados (${posRes.closed.length})`),
      h('div', { class: 'space-y-2' }, posRes.closed.slice(0, 20).map((p) => closedCard(p))),
    ]));
  }

  // ===== Configuración rápida =====
  wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('h3', { class: 'font-semibold text-sm mb-3' }, '⚙️ Profile de riesgo'),
    h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-2' }, ['conservador', 'moderado', 'agresivo', 'propfirm'].map((p) => h('button', {
      class: `px-3 py-2 rounded-lg text-sm border ${acc.profile === p ? 'bg-gold-500/20 border-gold-400 text-gold-300' : 'bg-base-700/50 border-base-700 hover:border-gold-400/40'}`,
      onclick: async () => {
        await apiClient.post('/api/v1/trading/settings', { profile: p });
        toast(`Profile cambiado a ${p}`, 'success');
        await renderTrading(root);
      },
    }, p))),
    h('p', { class: 'text-xs text-ink-400 mt-3' }, [
      'Conservador: 0.5%/trade, kill -2% día, -5% sem · ',
      'Moderado: 1%/trade, -3%, -8% · ',
      'Agresivo: 2%/trade, -5%, -12% · ',
      'Propfirm: 1%/trade, -4%, -8% (FTMO-style).',
    ]),
  ]));

  // ===== Backtest + Polymarket panel =====
  wrap.appendChild(h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-3' }, [
    h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h3', { class: 'font-semibold text-sm mb-2' }, '🔬 Backtest estrategia'),
      h('p', { class: 'text-xs text-ink-400 mb-3' }, 'Simula la estrategia multi-TF sobre histórico real. Mostrará winrate, profit factor, sharpe y drawdown esperado.'),
      h('div', { class: 'grid grid-cols-3 gap-2' }, ['XAUUSD', 'EURUSD', 'BTCUSD'].map((sym) => h('button', {
        class: 'px-3 py-2 rounded-lg bg-base-700/50 hover:bg-base-700 border border-base-700 hover:border-gold-400/40 text-sm',
        onclick: async () => {
          const t = toast(`Backtesting ${sym}... (puede tardar 5-10s)`);
          try {
            const r = await apiClient.post('/api/v1/trading/backtest', { symbol: sym, startingBalance: 10000, riskPerTradePct: 1, bars: 3000 });
            t.update(`${sym}: ${r.totalTrades} trades, WR ${r.winRatePct.toFixed(1)}%, PF ${r.profitFactor.toFixed(2)}, ROI ${r.roiPct.toFixed(1)}%`, r.netPnl > 0 ? 'success' : 'warning');
            renderBacktestResultModal(sym, r);
          } catch (e) { t.update('Backtest falló: ' + (e?.message || e), 'error'); }
        },
      }, sym))),
    ]),
    h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h3', { class: 'font-semibold text-sm mb-2' }, '🎯 Polymarket Top Mispricing'),
      h('p', { class: 'text-xs text-ink-400 mb-3' }, 'Mercados de predicción con mejor edge detectado. Lee-only (operar requiere wallet Polygon).'),
      h('button', {
        class: 'w-full px-3 py-2 rounded-lg bg-base-700/50 hover:bg-base-700 border border-base-700 hover:border-gold-400/40 text-sm',
        onclick: async () => {
          const t = toast('Buscando mispricing...');
          try {
            const r = await apiClient.get('/api/v1/trading/polymarket/top?top=15');
            t.update(`${r.candidates.length} candidatos detectados`, 'success');
            renderPolymarketModal(r.candidates);
          } catch (e) { t.update('Polymarket falló: ' + (e?.message || e), 'error'); }
        },
      }, '🔎 Escanear mercados activos'),
    ]),
  ]));

  // ===== Comparador propfirms / brokers / crypto =====
  const propfirmsRes = await apiClient.get('/api/v1/trading/propfirms?ranked=1').catch(() => ({ items: [] }));
  if (propfirmsRes.items && propfirmsRes.items.length > 0) {
    const top = propfirmsRes.items.slice(0, 6);
    wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('div', { class: 'flex items-center justify-between mb-3' }, [
        h('h3', { class: 'font-semibold text-sm' }, '🏆 Comparador Propfirms (ranked)'),
        h('a', { href: '#', onclick: (e) => { e.preventDefault(); renderAllPropfirms(root); }, class: 'text-xs text-gold-300 hover:underline' }, 'Ver todos →'),
      ]),
      h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' }, top.map((p) => propfirmCard(p))),
    ]));
  }

  // ===== Conexión MT4/MT5 (info) =====
  wrap.appendChild(h('div', { class: 'bg-emerald-900/10 border border-emerald-400/30 rounded-xl p-4' }, [
    h('h3', { class: 'font-semibold text-sm mb-2 text-emerald-300' }, '🔌 Conectar a MT4/MT5 (broker o propfirm)'),
    h('p', { class: 'text-xs text-ink-300 mb-3' }, 'El bot puede operar tu cuenta MT5 real mediante el Expert Advisor incluido. Bridge HTTP: el EA consulta señales de tu cuenta paper y replica los trades en tu broker.'),
    h('div', { class: 'text-xs space-y-1' }, [
      h('div', {}, [h('span', { class: 'text-ink-400' }, 'Token cuenta: '), h('code', { class: 'bg-base-900 px-2 py-0.5 rounded text-gold-300' }, acc.id)]),
      h('div', {}, [h('span', { class: 'text-ink-400' }, 'Endpoint EA: '), h('code', { class: 'bg-base-900 px-2 py-0.5 rounded text-gold-300' }, `${window.location.origin}/api/v1/trading/ea/signals/${acc.id}`)]),
      h('div', { class: 'mt-2 text-ink-400' }, 'Descarga el EA: '),
      h('a', { href: '/static/CARLOS_OS_Bridge.mq5', download: true, class: 'inline-block mt-1 bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-md' }, '⬇️ CARLOS_OS_Bridge.mq5'),
    ]),
  ]));
}

function kpi(label, value, valueClass, subtitle) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl px-4 py-3' }, [
    h('div', { class: 'text-xs text-ink-400' }, label),
    h('div', { class: `text-xl font-bold ${valueClass}` }, value),
    subtitle && h('div', { class: 'text-xs text-ink-500' }, subtitle),
  ]);
}

function statBox(label, value, valueClass) {
  return h('div', { class: 'bg-base-900/50 border border-base-700 rounded-lg px-3 py-2' }, [
    h('div', { class: 'text-xs text-ink-400' }, label),
    h('div', { class: `font-bold ${valueClass}` }, value),
  ]);
}

function positionCard(p) {
  const sideColor = p.side === 'long' ? 'text-emerald-400' : 'text-red-400';
  const sideArrow = p.side === 'long' ? '▲' : '▼';
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-3 flex items-center justify-between gap-3' }, [
    h('div', { class: 'flex items-center gap-3' }, [
      h('div', { class: `text-lg ${sideColor} font-bold` }, sideArrow),
      h('div', {}, [
        h('div', { class: 'font-semibold' }, `${p.symbol} ${p.side.toUpperCase()}`),
        h('div', { class: 'text-xs text-ink-400' }, `Entry ${p.entryPrice.toFixed(5)} · SL ${p.stopLoss.toFixed(5)} · TP ${p.takeProfit.toFixed(5)} · Vol ${p.volume}`),
      ]),
    ]),
    h('div', { class: 'text-right' }, [
      h('div', { class: 'text-xs text-ink-400' }, `Risk ${(p.riskAmount).toFixed(2)}€`),
      h('div', { class: 'text-xs text-ink-400' }, p.trailingActivated ? '🎯 Trailing activo' : ''),
    ]),
  ]);
}

function signalCard(s) {
  const c = s.status === 'executed' ? 'border-emerald-400/30 bg-emerald-500/5' : s.status === 'skipped' ? 'border-base-700' : 'border-amber-400/30 bg-amber-500/5';
  return h('div', { class: `border rounded-lg p-3 ${c}` }, [
    h('div', { class: 'flex items-center justify-between mb-1' }, [
      h('span', { class: 'font-semibold' }, `${s.symbol} ${s.side.toUpperCase()} ${s.timeframe}`),
      h('span', { class: 'text-xs text-ink-400' }, `${new Date(s.createdAt).toLocaleString()}`),
    ]),
    h('div', { class: 'text-xs text-ink-300 space-y-0.5' }, [
      h('div', {}, `Entry ${s.entryPrice.toFixed(5)} · SL ${s.stopLoss.toFixed(5)} · TP ${s.takeProfit.toFixed(5)} · R:R ${s.rrRatio.toFixed(2)}`),
      h('div', {}, `Confianza ${(s.confidence * 100).toFixed(0)}% · EV ${s.expectedValue.toFixed(3)}`),
      h('div', { class: 'text-ink-400' }, Array.isArray(s.reasons) ? s.reasons.join(' · ') : ''),
      s.skippedReason && h('div', { class: 'text-amber-300' }, `⚠ ${s.skippedReason}`),
    ]),
  ]);
}

function renderBacktestResultModal(symbol, result) {
  const existing = document.getElementById('bt-modal');
  if (existing) existing.remove();
  const winColor = result.netPnl > 0 ? 'text-emerald-400' : 'text-red-400';
  const modal = h('div', { id: 'bt-modal', class: 'fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4', onclick: (e) => { if (e.target === modal) modal.remove(); } }, [
    h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-5 max-w-4xl w-full max-h-[90vh] overflow-y-auto' }, [
      h('div', { class: 'flex items-center justify-between mb-4' }, [
        h('h3', { class: 'font-display text-lg font-bold' }, `🔬 Backtest ${symbol}`),
        h('button', { class: 'text-ink-400 hover:text-ink-100 text-xl', onclick: () => modal.remove() }, '×'),
      ]),
      h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-2 mb-4' }, [
        statBox('Total Trades', result.totalTrades.toString(), 'text-ink-200'),
        statBox('Win Rate', `${result.winRatePct.toFixed(1)}%`, result.winRatePct >= 50 ? 'text-emerald-400' : 'text-amber-400'),
        statBox('Profit Factor', result.profitFactor.toFixed(2), result.profitFactor >= 1.3 ? 'text-emerald-400' : 'text-amber-400'),
        statBox('Sharpe', result.sharpe.toFixed(2), result.sharpe >= 1 ? 'text-emerald-400' : 'text-amber-400'),
        statBox('ROI', `${result.roiPct.toFixed(1)}%`, winColor),
        statBox('Net P&L', `€${result.netPnl.toFixed(0)}`, winColor),
        statBox('Max DD', `${result.maxDrawdownPct.toFixed(1)}%`, result.maxDrawdownPct < 15 ? 'text-amber-400' : 'text-red-400'),
        statBox('Expectancy', `€${result.expectancy.toFixed(2)}`, result.expectancy > 0 ? 'text-emerald-400' : 'text-red-400'),
      ]),
      h('div', { class: 'mb-4' }, [
        h('h4', { class: 'text-sm font-semibold mb-2' }, '📈 Equity Curve'),
        (() => {
          const cnv = h('div', { id: 'bt-equity-canvas', class: 'relative w-full', style: 'height: 300px' });
          setTimeout(() => renderBacktestEquityChart(cnv, result.equityCurve, result.trades).catch(() => null), 100);
          return cnv;
        })(),
      ]),
      h('div', {}, [
        h('h4', { class: 'text-sm font-semibold mb-2' }, `📋 Últimos trades (${result.trades.length}/${result.tradesCount || result.totalTrades})`),
        h('div', { class: 'max-h-64 overflow-y-auto space-y-1' }, (result.trades || []).map((t) => {
          const ok = t.pnl > 0;
          return h('div', { class: `border rounded p-1.5 text-xs flex items-center justify-between ${ok ? 'border-emerald-400/30 bg-emerald-500/5' : 'border-red-400/30 bg-red-500/5'}` }, [
            h('div', {}, [
              h('span', { class: 'font-semibold' }, `${t.side.toUpperCase()} `),
              h('span', { class: 'text-ink-400' }, `${t.entry.toFixed(4)} → ${t.exit.toFixed(4)} `),
              h('span', { class: 'text-ink-500' }, t.reason),
            ]),
            h('span', { class: ok ? 'text-emerald-400 font-mono' : 'text-red-400 font-mono' }, `${ok ? '+' : ''}€${t.pnl.toFixed(2)}`),
          ]);
        })),
      ]),
    ]),
  ]);
  document.body.appendChild(modal);
}

function renderPolymarketModal(candidates) {
  const existing = document.getElementById('pm-modal');
  if (existing) existing.remove();
  const modal = h('div', { id: 'pm-modal', class: 'fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4', onclick: (e) => { if (e.target === modal) modal.remove(); } }, [
    h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-5 max-w-3xl w-full max-h-[85vh] overflow-y-auto' }, [
      h('div', { class: 'flex items-center justify-between mb-3' }, [
        h('h3', { class: 'font-display text-lg font-bold' }, '🎯 Polymarket — Top Mispricing'),
        h('button', { class: 'text-ink-400 hover:text-ink-100 text-xl', onclick: () => modal.remove() }, '×'),
      ]),
      h('div', { class: 'space-y-2' }, candidates.map((c) => h('div', { class: 'border border-base-700 rounded-lg p-3 hover:border-gold-400/40' }, [
        h('div', { class: 'flex items-start justify-between gap-3' }, [
          h('div', { class: 'flex-1' }, [
            h('div', { class: 'font-semibold text-sm' }, c.market.question),
            h('div', { class: 'text-xs text-ink-400 mt-1' }, `${c.market.category ?? 'general'} · ${c.daysToResolution.toFixed(0)} días`),
            h('div', { class: 'text-xs mt-1' }, [
              h('span', { class: 'text-ink-400' }, 'YES: '),
              h('span', { class: 'text-emerald-300 font-mono' }, `${(c.impliedProbabilityYes * 100).toFixed(1)}%`),
              h('span', { class: 'text-ink-400 ml-3' }, 'Vol 24h: '),
              h('span', { class: 'text-gold-300 font-mono' }, `$${(c.market.volume24h ?? 0).toFixed(0)}`),
            ]),
          ]),
          h('div', { class: 'text-right' }, [
            h('div', { class: 'text-2xl font-bold text-gold-400' }, c.score.toFixed(0)),
            h('div', { class: 'text-xs text-ink-400' }, 'score'),
            c.market.url && h('a', { href: c.market.url, target: '_blank', rel: 'noopener', class: 'inline-block mt-1 text-xs text-gold-300 hover:underline' }, 'Abrir ↗'),
          ]),
        ]),
      ]))),
    ]),
  ]);
  document.body.appendChild(modal);
}

function propfirmCard(p) {
  const trackColor = p.paymentTrackRecord === 'excellent' ? 'text-emerald-400' :
                     p.paymentTrackRecord === 'good' ? 'text-emerald-300' :
                     p.paymentTrackRecord === 'mixed' ? 'text-amber-400' :
                     p.paymentTrackRecord === 'poor' ? 'text-red-400' : 'text-ink-400';
  const stars = '★'.repeat(Math.round(p.trustpilotScore || 0)) + '☆'.repeat(5 - Math.round(p.trustpilotScore || 0));
  return h('div', { class: 'border border-base-700 bg-base-900/50 rounded-lg p-3 hover:border-gold-400/40 transition' }, [
    h('div', { class: 'flex items-center justify-between mb-2' }, [
      h('div', {}, [
        h('div', { class: 'font-semibold' }, p.name),
        h('div', { class: 'text-xs text-ink-400' }, `${p.hq} · founded ${p.founded}`),
      ]),
      p.website && h('a', { href: p.website, target: '_blank', rel: 'noopener', class: 'text-xs text-gold-300 hover:underline' }, '↗'),
    ]),
    h('div', { class: 'text-xs text-ink-300 space-y-0.5' }, [
      p.evaluationFeeEur != null && h('div', {}, [h('span', { class: 'text-ink-400' }, 'Fee: '), `€${p.evaluationFeeEur}`]),
      p.profitSplitTraderPct != null && h('div', {}, [h('span', { class: 'text-ink-400' }, 'Profit split: '), `${p.profitSplitTraderPct}%`]),
      p.profitTargetPct != null && h('div', {}, [h('span', { class: 'text-ink-400' }, 'Target: '), `${p.profitTargetPct}% / DD ${p.maxOverallLossPct}%`]),
      p.payoutFrequency && h('div', {}, [h('span', { class: 'text-ink-400' }, 'Payout: '), p.payoutFrequency]),
      p.trustpilotScore != null && h('div', {}, [
        h('span', { class: 'text-ink-400' }, 'TrustPilot: '),
        h('span', { class: 'text-amber-300' }, stars),
        h('span', { class: 'text-ink-400' }, ` ${p.trustpilotScore.toFixed(1)} (${p.trustpilotReviews})`),
      ]),
      h('div', {}, [h('span', { class: 'text-ink-400' }, 'Pagos: '), h('span', { class: trackColor }, p.paymentTrackRecord)]),
    ]),
  ]);
}

async function renderAllPropfirms(root) {
  const all = await apiClient.get('/api/v1/trading/propfirms').catch(() => ({ items: [] }));
  root.innerHTML = '';
  const wrap = h('div', { class: 'max-w-7xl mx-auto px-6 py-6 space-y-6' });
  root.appendChild(wrap);
  wrap.appendChild(h('div', { class: 'flex items-center justify-between' }, [
    h('h2', { class: 'font-display text-2xl font-bold' }, '🏆 Todos los Propfirms, Brokers & Exchanges'),
    h('button', { class: 'text-sm text-gold-300 hover:underline', onclick: () => renderTrading(root) }, '← Volver'),
  ]));
  // Tabs
  const groups = [
    { key: 'propfirm', label: '🏛️ Propfirms', items: all.items.filter((i) => i.type === 'propfirm') },
    { key: 'broker',   label: '🏦 Brokers',   items: all.items.filter((i) => i.type === 'broker') },
    { key: 'crypto',   label: '₿ Crypto Exchanges', items: all.items.filter((i) => i.type === 'crypto_exchange') },
  ];
  groups.forEach((g) => {
    wrap.appendChild(h('div', {}, [
      h('h3', { class: 'font-semibold mb-2 text-base' }, `${g.label} (${g.items.length})`),
      h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' }, g.items.map((p) => propfirmCard(p))),
    ]));
  });
}

function closedCard(p) {
  const win = (p.realizedPnl ?? 0) > 0;
  const c = win ? 'border-emerald-400/30 bg-emerald-500/5' : 'border-red-400/30 bg-red-500/5';
  return h('div', { class: `border rounded-lg p-2 ${c} flex items-center justify-between text-sm` }, [
    h('div', { class: 'flex items-center gap-2' }, [
      h('span', { class: `font-bold ${win ? 'text-emerald-400' : 'text-red-400'}` }, win ? '✓' : '✗'),
      h('span', {}, `${p.symbol} ${p.side.toUpperCase()}`),
      h('span', { class: 'text-ink-400 text-xs' }, `${new Date(p.openedAt).toLocaleDateString()} → ${p.closedAt ? new Date(p.closedAt).toLocaleDateString() : '—'}`),
      h('span', { class: 'text-ink-400 text-xs' }, p.exitReason ?? '—'),
    ]),
    h('div', { class: `font-bold ${win ? 'text-emerald-400' : 'text-red-400'}` }, `${(p.realizedPnl ?? 0) >= 0 ? '+' : ''}${formatEur(p.realizedPnl ?? 0)}`),
  ]);
}

// ====== HISTORIAL COMPLETO DE OPERACIONES ======
async function renderHistoryPanel(parent) {
  const box = h('div', { class: 'rounded-xl border border-base-700 bg-base-900/60 p-5 mt-6' });
  box.appendChild(h('div', { class: 'flex items-center justify-between mb-4 flex-wrap gap-2' }, [
    h('h3', { class: 'font-display text-xl font-bold' }, '📊 Historial completo de operaciones'),
    h('div', { id: 'hist-stats', class: 'text-xs text-ink-400' }, 'Cargando...'),
  ]));
  // Filtros
  const filterBox = h('div', { class: 'flex flex-wrap gap-2 mb-4' }, [
    h('select', { id: 'f-symbol', class: 'bg-base-800 border border-base-600 rounded px-2 py-1 text-sm' }, [
      h('option', { value: '' }, 'Todos los símbolos'),
      h('option', { value: 'BTCUSD' }, 'BTCUSD'),
      h('option', { value: 'ETHUSD' }, 'ETHUSD'),
      h('option', { value: 'EURUSD' }, 'EURUSD'),
      h('option', { value: 'GBPUSD' }, 'GBPUSD'),
    ]),
    h('select', { id: 'f-status', class: 'bg-base-800 border border-base-600 rounded px-2 py-1 text-sm' }, [
      h('option', { value: '' }, 'Todos los estados'),
      h('option', { value: 'open' }, '🟢 Abiertas'),
      h('option', { value: 'closed' }, '✓ Cerradas'),
    ]),
    h('select', { id: 'f-side', class: 'bg-base-800 border border-base-600 rounded px-2 py-1 text-sm' }, [
      h('option', { value: '' }, 'Long + Short'),
      h('option', { value: 'long' }, '↑ Long'),
      h('option', { value: 'short' }, '↓ Short'),
    ]),
    h('input', { id: 'f-from', type: 'date', class: 'bg-base-800 border border-base-600 rounded px-2 py-1 text-sm' }),
    h('input', { id: 'f-to', type: 'date', class: 'bg-base-800 border border-base-600 rounded px-2 py-1 text-sm' }),
    h('button', { id: 'f-apply', class: 'text-sm px-3 py-1 bg-gold-500 hover:bg-gold-400 text-base-900 font-semibold rounded' }, 'Filtrar'),
  ]);
  box.appendChild(filterBox);
  box.appendChild(h('div', { id: 'hist-table', class: 'overflow-x-auto' }));
  box.appendChild(h('div', { id: 'hist-by-symbol', class: 'mt-6' }));
  parent.appendChild(box);

  async function load() {
    const params = new URLSearchParams();
    const sym = filterBox.querySelector('#f-symbol').value;
    const st  = filterBox.querySelector('#f-status').value;
    const sd  = filterBox.querySelector('#f-side').value;
    const fr  = filterBox.querySelector('#f-from').value;
    const to  = filterBox.querySelector('#f-to').value;
    if (sym) params.set('symbol', sym);
    if (st)  params.set('status', st);
    if (sd)  params.set('side', sd);
    if (fr)  params.set('from', fr);
    if (to)  params.set('to', to);
    params.set('limit', '300');
    const path = '/api/v1/trading/history?' + params.toString();
    box.querySelector('#hist-table').innerHTML = '<div class="text-ink-400 text-sm py-4">Cargando...</div>';
    try {
      const r = await apiClient.get(path);
      renderTable(box, r);
    } catch (e) {
      box.querySelector('#hist-table').innerHTML = '';
      box.querySelector('#hist-table').appendChild(h('div', { class: 'text-rose-400 text-sm py-4' }, 'Error: ' + (e?.message || e)));
    }
  }
  filterBox.querySelector('#f-apply').addEventListener('click', load);
  await load();
}

function renderTable(box, r) {
  const stats = r.stats || {};
  const winColor = stats.totalPnl >= 0 ? 'text-emerald-400' : 'text-rose-400';
  box.querySelector('#hist-stats').textContent = `${r.total} ops · ${r.open} abiertas · ${r.closed} cerradas · WR ${stats.winRate}% · PnL ${stats.totalPnl >= 0 ? '+' : ''}${stats.totalPnl.toFixed(2)}€ · PF ${stats.profitFactor || '—'}`;

  const tbl = h('table', { class: 'w-full text-sm' });
  tbl.appendChild(h('thead', { class: 'text-xs uppercase tracking-wider text-ink-400 border-b border-base-700' }, [
    h('tr', {}, [
      h('th', { class: 'text-left px-2 py-2' }, 'Abierta'),
      h('th', { class: 'text-left px-2 py-2' }, 'Símbolo'),
      h('th', { class: 'text-left px-2 py-2' }, 'Lado'),
      h('th', { class: 'text-left px-2 py-2' }, 'TF'),
      h('th', { class: 'text-right px-2 py-2' }, 'Entry'),
      h('th', { class: 'text-right px-2 py-2' }, 'SL'),
      h('th', { class: 'text-right px-2 py-2' }, 'TP'),
      h('th', { class: 'text-right px-2 py-2' }, 'Exit'),
      h('th', { class: 'text-right px-2 py-2' }, 'PnL'),
      h('th', { class: 'text-right px-2 py-2' }, 'RR'),
      h('th', { class: 'text-left px-2 py-2' }, 'Motivo cierre'),
      h('th', { class: 'text-right px-2 py-2' }, 'Duración'),
    ]),
  ]));
  const tbody = h('tbody', {});
  for (const p of r.positions) {
    const win = (p.realizedPnl || 0) > 0;
    const pnlColor = p.status === 'open' ? 'text-amber-300' : win ? 'text-emerald-400' : 'text-rose-400';
    const sideEmoji = p.side === 'long' ? '↑' : '↓';
    const row = h('tr', { class: 'border-b border-base-800 hover:bg-base-800/50 cursor-pointer' });
    row.appendChild(h('td', { class: 'px-2 py-2 text-ink-300' }, new Date(p.openedAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })));
    row.appendChild(h('td', { class: 'px-2 py-2 font-mono' }, p.symbol));
    row.appendChild(h('td', { class: 'px-2 py-2' }, `${sideEmoji} ${p.side.toUpperCase()}`));
    row.appendChild(h('td', { class: 'px-2 py-2 text-ink-400' }, p.timeframe || '—'));
    row.appendChild(h('td', { class: 'px-2 py-2 text-right font-mono text-xs' }, fmtPrice(p.entryPrice)));
    row.appendChild(h('td', { class: 'px-2 py-2 text-right font-mono text-xs text-rose-400/70' }, fmtPrice(p.stopLoss)));
    row.appendChild(h('td', { class: 'px-2 py-2 text-right font-mono text-xs text-emerald-400/70' }, fmtPrice(p.takeProfit)));
    row.appendChild(h('td', { class: 'px-2 py-2 text-right font-mono text-xs' }, p.exitPrice ? fmtPrice(p.exitPrice) : '—'));
    row.appendChild(h('td', { class: `px-2 py-2 text-right font-bold ${pnlColor}` }, p.status === 'open' ? '🟢 abierta' : `${(p.realizedPnl || 0) >= 0 ? '+' : ''}${(p.realizedPnl || 0).toFixed(2)}€`));
    row.appendChild(h('td', { class: 'px-2 py-2 text-right text-xs text-ink-300' }, p.rrAchieved ? p.rrAchieved + 'R' : '—'));
    row.appendChild(h('td', { class: 'px-2 py-2 text-xs text-ink-400' }, p.exitReason || '—'));
    row.appendChild(h('td', { class: 'px-2 py-2 text-right text-xs text-ink-400' }, p.durationHours + 'h'));
    row.addEventListener('click', () => openDetailModal(p));
    tbody.appendChild(row);
  }
  tbl.appendChild(tbody);
  box.querySelector('#hist-table').innerHTML = '';
  box.querySelector('#hist-table').appendChild(tbl);

  // Stats por símbolo
  const bySym = box.querySelector('#hist-by-symbol');
  bySym.innerHTML = '';
  if (r.bySymbol && Object.keys(r.bySymbol).length) {
    bySym.appendChild(h('h4', { class: 'text-sm font-semibold text-ink-300 mb-2' }, 'Por símbolo'));
    bySym.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-2' },
      Object.entries(r.bySymbol).map(([sym, s]) => h('div', { class: 'rounded border border-base-700 bg-base-800/40 p-3' }, [
        h('div', { class: 'font-mono text-sm font-bold' }, sym),
        h('div', { class: `text-lg font-bold ${s.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}` }, `${s.pnl >= 0 ? '+' : ''}${s.pnl.toFixed(2)}€`),
        h('div', { class: 'text-xs text-ink-400' }, `${s.count} ops · ${(s.winRate * 100).toFixed(0)}% WR · ${s.wins}W/${s.losses}L`),
      ]))
    ));
  }
}

function openDetailModal(p) {
  const win = (p.realizedPnl || 0) > 0;
  const backdrop = h('div', { class: 'fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4', onclick: (e) => { if (e.target === backdrop) backdrop.remove(); } });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto' });
  modal.appendChild(h('div', { class: 'flex items-center justify-between mb-4' }, [
    h('h3', { class: 'text-xl font-bold' }, `${p.symbol} · ${p.side.toUpperCase()} · ${p.timeframe || ''}`),
    h('button', { class: 'text-ink-400 hover:text-white text-2xl leading-none', onclick: () => backdrop.remove() }, '×'),
  ]));
  modal.appendChild(h('div', { class: 'grid grid-cols-2 gap-3 mb-4' }, [
    field('Estado', p.status === 'open' ? '🟢 ABIERTA' : '✓ cerrada'),
    field('P&L', p.status === 'open' ? 'live' : `${(p.realizedPnl || 0) >= 0 ? '+' : ''}${(p.realizedPnl || 0).toFixed(2)}€`, win ? 'text-emerald-400' : p.status === 'open' ? 'text-amber-300' : 'text-rose-400'),
    field('P&L %', p.pnlPct ? p.pnlPct.toFixed(2) + '%' : '—'),
    field('RR conseguido', p.rrAchieved ? p.rrAchieved + 'R' : '—'),
    field('Entry price', fmtPrice(p.entryPrice)),
    field('Exit price', p.exitPrice ? fmtPrice(p.exitPrice) : '—'),
    field('Stop Loss inicial', fmtPrice(p.initialStopLoss)),
    field('Stop Loss actual', fmtPrice(p.stopLoss)),
    field('Take Profit', fmtPrice(p.takeProfit)),
    field('Volumen', String(p.volume)),
    field('Apalancamiento', p.leverage + 'x'),
    field('Riesgo €', p.riskAmount.toFixed(2) + '€'),
    field('Comisión', (p.commission || 0).toFixed(4) + '€'),
    field('Swap acumulado', (p.swapAccumulated || 0).toFixed(4) + '€'),
    field('Spread entry', String(p.spreadAtEntry || 0)),
    field('Trailing activado', p.trailingActivated ? '✓ sí' : '✗ no'),
    field('Highest price', p.highestPrice ? fmtPrice(p.highestPrice) : '—'),
    field('Lowest price', p.lowestPrice ? fmtPrice(p.lowestPrice) : '—'),
    field('Abierta', new Date(p.openedAt).toLocaleString('es-ES')),
    field('Cerrada', p.closedAt ? new Date(p.closedAt).toLocaleString('es-ES') : '—'),
    field('Duración', p.durationHours + 'h'),
    field('Motivo cierre', p.exitReason || '—'),
  ]));
  if (p.signalReasons && p.signalReasons.length) {
    modal.appendChild(h('div', { class: 'mt-4 pt-4 border-t border-base-700' }, [
      h('h4', { class: 'font-semibold mb-2' }, '📡 Señal que la disparó'),
      h('div', { class: 'grid grid-cols-3 gap-2 mb-2' }, [
        field('Confidence', p.signalConfidence ? (p.signalConfidence * 100).toFixed(0) + '%' : '—'),
        field('Expected Value', p.signalExpectedValue ? p.signalExpectedValue.toFixed(2) : '—'),
        field('RR planificado', p.signalRrPlanned ? p.signalRrPlanned + 'R' : '—'),
      ]),
      h('div', { class: 'text-xs text-ink-300' }, 'Razones:'),
      h('ul', { class: 'list-disc pl-5 text-sm text-ink-200 space-y-1' }, p.signalReasons.map((r) => h('li', {}, r))),
    ]));
  }
  if (p.signalIndicators) {
    modal.appendChild(h('details', { class: 'mt-4' }, [
      h('summary', { class: 'cursor-pointer text-sm text-gold-400' }, 'Ver indicadores técnicos completos'),
      h('pre', { class: 'mt-2 text-xs bg-base-800 p-3 rounded overflow-x-auto' }, JSON.stringify(p.signalIndicators, null, 2)),
    ]));
  }
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
}

function field(label, value, color) {
  return h('div', { class: 'bg-base-800/50 rounded p-2' }, [
    h('div', { class: 'text-xs uppercase tracking-wider text-ink-400' }, label),
    h('div', { class: `text-sm font-mono ${color || 'text-ink-100'}` }, value),
  ]);
}

function fmtPrice(p) {
  if (p == null) return '—';
  const n = Number(p);
  if (n >= 1000) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  return n.toFixed(5);
}

// Call this at the end of renderTrading to attach the history panel
export async function appendHistoryPanel(wrap) {
  await renderHistoryPanel(wrap);
}
